import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { ContentSettings } from '@models/settings/content-settings';
import { ContentSettingsQueryService } from '@query/contentSettingsQueryService';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  private readonly _contentSettingsQuery = inject(ContentSettingsQueryService);
  private readonly _createMutation = this._contentSettingsQuery.createContentSettings();

  private readonly _query = this._contentSettingsQuery.getContentSettings();

  private readonly _settingsSignal = signal<ContentSettings | undefined>(undefined);

  private readonly _currentSettings = computed(() => {
    const data = this._query.data();
    return data?.results?.[0];
  });

  public readonly settings = computed(() => {
    return this._settingsSignal() || this._currentSettings();
  });

  public readonly isLoaded = computed(() => {
    return !this._query.isLoading() && this._query.isFetched();
  });

  constructor() {
    effect(() => {
      const settings = this._currentSettings();
      const isLoaded = this.isLoaded();

      if (isLoaded) {
        if (settings) {
          this._settingsSignal.set(settings);
        } else {
          this._createDefaultSettings();
        }
      }
    });
  }

  private _getDefaultSettings(): ContentSettings {
    return {
      uuid: '',
      allow_content_edit: false,
    } as ContentSettings;
  }

  private _createDefaultSettings(): void {
    this._createMutation.mutate(
      {
        allow_content_edit: false,
      },
      {
        onSuccess: response => {
          this._settingsSignal.set(response);
        },
        onError: () => {
          this._settingsSignal.set(this._getDefaultSettings());
        },
      }
    );
  }

  public async loadSettings(): Promise<void> {
    return Promise.resolve();
  }

  public getSettings(): ContentSettings {
    return this.settings() || this._getDefaultSettings();
  }

  public setSettings(settings: ContentSettings): void {
    this._settingsSignal.set(settings);
  }
}
